import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { getMonthlyReport, getYearlySummary, compareEntries } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LineChart, Line } from 'recharts';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

const monthNames = ['Ιαν', 'Φεβ', 'Μαρ', 'Απρ', 'Μαΐ', 'Ιουν', 'Ιουλ', 'Αυγ', 'Σεπ', 'Οκτ', 'Νοε', 'Δεκ'];

export default function Reports() {
  const { selectedFactory } = useAuth();
  const [tab, setTab] = useState('monthly');

  // Monthly
  const [mYear, setMYear] = useState(new Date().getFullYear());
  const [mMonth, setMMonth] = useState(new Date().getMonth() + 1);
  const [monthData, setMonthData] = useState(null);
  const [mLoading, setMLoading] = useState(false);

  // Yearly
  const [yYear, setYYear] = useState(new Date().getFullYear());
  const [yearData, setYearData] = useState(null);
  const [yLoading, setYLoading] = useState(false);

  // Compare
  const [compareDates, setCompareDates] = useState(['', '']);
  const [compareData, setCompareData] = useState(null);
  const [cLoading, setCLoading] = useState(false);

  const loadMonthly = async () => {
    if (!selectedFactory) return;
    setMLoading(true);
    try {
      const res = await getMonthlyReport(selectedFactory.id, mYear, mMonth);
      setMonthData(res.data);
    } catch (err) { console.error(err); }
    finally { setMLoading(false); }
  };

  const loadYearly = async () => {
    if (!selectedFactory) return;
    setYLoading(true);
    try {
      const res = await getYearlySummary(selectedFactory.id, yYear);
      setYearData(res.data);
    } catch (err) { console.error(err); }
    finally { setYLoading(false); }
  };

  const loadCompare = async () => {
    if (!selectedFactory) return;
    const valid = compareDates.filter(Boolean);
    if (valid.length < 2) return;
    setCLoading(true);
    try {
      const res = await compareEntries(selectedFactory.id, valid);
      setCompareData(res.data);
    } catch (err) { console.error(err); }
    finally { setCLoading(false); }
  };

  const yearChartData = yearData?.monthly_breakdown?.map(r => ({
    month: monthNames[r.month - 1],
    total: parseFloat(r.monthly_total),
    days: parseInt(r.working_days)
  })) || [];

  const dailyChartData = monthData?.daily_breakdown?.map(d => ({
    day: parseInt(d.entry_date?.split('-')[2]),
    total: parseFloat(d.daily_total)
  })) || [];

  return (
    <>
      <div className="page-header">
        <h2>Αναφορές</h2>
        <p>Αναλυτικά στοιχεία παραγωγής και εσόδων</p>
      </div>
      <div className="page-body">
        <div className="tabs">
          {[['monthly', '📅 Μηνιαία'], ['yearly', '📆 Ετήσια'], ['compare', '⚖️ Σύγκριση']].map(([key, label]) => (
            <button key={key} className={`tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>{label}</button>
          ))}
        </div>

        {/* MONTHLY */}
        {tab === 'monthly' && (
          <div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-body" style={{ padding: '14px 20px' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Έτος</label>
                    <input type="number" className="form-input" value={mYear} onChange={e => setMYear(parseInt(e.target.value))} style={{ width: 100 }} />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Μήνας</label>
                    <select className="form-select" value={mMonth} onChange={e => setMMonth(parseInt(e.target.value))} style={{ width: 130 }}>
                      {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <button className="btn btn-primary" onClick={loadMonthly} disabled={mLoading}>
                    {mLoading ? <span className="loading-spinner" /> : 'Εμφάνιση'}
                  </button>
                </div>
              </div>
            </div>

            {monthData && (
              <>
                <div className="stats-grid" style={{ marginBottom: 20 }}>
                  <div className="stat-card">
                    <div className="stat-card-label">Σύνολο Μήνα</div>
                    <div className="stat-card-value blue">€{parseFloat(monthData.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</div>
                    <div className="stat-card-sub">{monthNames[monthData.month - 1]} {monthData.year}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Εργάσιμες Μέρες</div>
                    <div className="stat-card-value">{monthData.daily_breakdown?.length || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Μ.Ο. Ημέρας</div>
                    <div className="stat-card-value">
                      {monthData.daily_breakdown?.length
                        ? `€${(parseFloat(monthData.grand_total) / monthData.daily_breakdown.length).toFixed(0)}`
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="grid-2">
                  <div className="card">
                    <div className="card-header"><span className="card-title">Ημερήσια Έσοδα</span></div>
                    <div className="card-body" style={{ padding: '16px 8px' }}>
                      <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={dailyChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                          <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                          <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `€${v}`} width={55} />
                          <Tooltip formatter={v => [`€${v.toFixed(2)}`, 'Σύνολο']} labelFormatter={l => `Ημέρα ${l}`} />
                          <Bar dataKey="total" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header"><span className="card-title">Ανά Προϊόν</span></div>
                    <div className="table-wrap">
                      <table>
                        <thead><tr><th>Προϊόν</th><th>Τεμ.</th><th>Σύνολο</th></tr></thead>
                        <tbody>
                          {monthData.product_breakdown?.map((p, i) => (
                            <tr key={i}>
                              <td><strong>{p.product_name}</strong></td>
                              <td>{parseInt(p.total_quantity).toLocaleString('el-GR')}</td>
                              <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(p.total_revenue).toFixed(2)}</strong></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* YEARLY */}
        {tab === 'yearly' && (
          <div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-body" style={{ padding: '14px 20px' }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Έτος</label>
                    <input type="number" className="form-input" value={yYear} onChange={e => setYYear(parseInt(e.target.value))} style={{ width: 100 }} />
                  </div>
                  <button className="btn btn-primary" onClick={loadYearly} disabled={yLoading}>
                    {yLoading ? <span className="loading-spinner" /> : 'Εμφάνιση'}
                  </button>
                </div>
              </div>
            </div>

            {yearData && (
              <>
                <div className="stats-grid" style={{ marginBottom: 20 }}>
                  <div className="stat-card">
                    <div className="stat-card-label">Σύνολο Έτους</div>
                    <div className="stat-card-value blue">€{parseFloat(yearData.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</div>
                    <div className="stat-card-sub">{yearData.year}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Ενεργοί Μήνες</div>
                    <div className="stat-card-value">{yearData.monthly_breakdown?.length || 0}</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-card-label">Καλύτερος Μήνας</div>
                    <div className="stat-card-value" style={{ fontSize: 18 }}>
                      {yearData.monthly_breakdown?.length
                        ? monthNames[yearData.monthly_breakdown.reduce((a, b) => parseFloat(a.monthly_total) > parseFloat(b.monthly_total) ? a : b).month - 1]
                        : '—'}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header"><span className="card-title">Μηνιαία Έσοδα {yearData.year}</span></div>
                  <div className="card-body" style={{ padding: '16px 8px' }}>
                    <ResponsiveContainer width="100%" height={260}>
                      <BarChart data={yearChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#94A3B8' }} />
                        <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={v => `€${v}`} width={60} />
                        <Tooltip formatter={v => [`€${v.toFixed(2)}`, 'Σύνολο']} />
                        <Bar dataKey="total" fill="#2563EB" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="card" style={{ marginTop: 20 }}>
                  <div className="card-header"><span className="card-title">Μηνιαία Ανάλυση</span></div>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Μήνας</th><th>Εργάσιμες</th><th>Σύνολο</th><th>Μ.Ο. Ημέρας</th></tr></thead>
                      <tbody>
                        {yearData.monthly_breakdown?.map(r => (
                          <tr key={r.month}>
                            <td><strong>{monthNames[r.month - 1]} {yearData.year}</strong></td>
                            <td>{r.working_days} μέρες</td>
                            <td><strong style={{ color: 'var(--blue-dark)' }}>€{parseFloat(r.monthly_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</strong></td>
                            <td style={{ color: 'var(--gray-500)' }}>€{(parseFloat(r.monthly_total) / r.working_days).toFixed(0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* COMPARE */}
        {tab === 'compare' && (
          <div>
            <div className="card" style={{ marginBottom: 20 }}>
              <div className="card-header"><span className="card-title">Επιλογή Ημερομηνιών για Σύγκριση</span></div>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  {compareDates.map((d, i) => (
                    <div key={i} className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Ημερομηνία {i + 1}</label>
                      <input type="date" className="form-input" value={d} onChange={e => {
                        const nd = [...compareDates]; nd[i] = e.target.value; setCompareDates(nd);
                      }} />
                    </div>
                  ))}
                  {compareDates.length < 5 && (
                    <button className="btn btn-secondary" onClick={() => setCompareDates([...compareDates, ''])}>+ Ημερομηνία</button>
                  )}
                  <button className="btn btn-primary" onClick={loadCompare} disabled={cLoading}>
                    {cLoading ? <span className="loading-spinner" /> : 'Σύγκριση'}
                  </button>
                </div>
              </div>
            </div>

            {compareData && (
              <div className="grid-2">
                {compareData.map((entry, i) => (
                  <div key={i} className="card">
                    <div className="card-header">
                      <span className="card-title">{format(new Date(entry.date), 'd MMMM yyyy', { locale: el })}</span>
                      <span className="badge badge-blue">€{parseFloat(entry.grand_total).toLocaleString('el-GR', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="table-wrap">
                      {entry.items.length === 0 ? (
                        <div className="empty-state" style={{ padding: 30 }}><p>Δεν υπάρχουν δεδομένα</p></div>
                      ) : (
                        <table>
                          <thead><tr><th>Προϊόν</th><th>Τεμ.</th><th>Σύνολο</th></tr></thead>
                          <tbody>
                            {entry.items.map((item, j) => (
                              <tr key={j}>
                                <td>{item.product_name}</td>
                                <td>{item.quantity}</td>
                                <td>€{parseFloat(item.total_price).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
